import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex items-center" }
const _hoisted_2 = { class: "relative" }
const _hoisted_3 = {
  key: 0,
  class: "absolute -top-1 -right-1 bg-negative text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 outline outline-2 outline-white"
}
const _hoisted_4 = { class: "absolute right-0 mt-2 w-[24rem] bg-white rounded-xl shadow-md overflow-hidden z-30 border border-gray-200/80" }
const _hoisted_5 = { class: "flex justify-between p-4 items-center" }
const _hoisted_6 = { class: "flex items-center gap-2" }
const _hoisted_7 = {
  key: 0,
  class: "bg-negative text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
}
const _hoisted_8 = /*#__PURE__*/_createElementVNode("h3", { class: "text-lg font-bold text-textPrimary" }, "Notificações", -1)
const _hoisted_9 = { class: "flex items-center gap-4" }
const _hoisted_10 = {
  key: 0,
  class: "max-h-[28rem] overflow-y-auto"
}
const _hoisted_11 = {
  key: 1,
  class: "max-h-[28rem] overflow-y-auto"
}
const _hoisted_12 = { class: "flex-shrink-0 flex items-center justify-center p-2 gap-2 rounded-full bg-primary30 mt-1" }
const _hoisted_13 = { class: "flex-1 min-w-0 text-left" }
const _hoisted_14 = ["onClick"]
const _hoisted_15 = ["onClick"]
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "flex items-center gap-2 mt-1" }
const _hoisted_18 = { class: "text-xs font-normal text-[#68688D] leading-[16px] tracking-[0.07px]" }
const _hoisted_19 = {
  key: 0,
  class: "absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-negative flex-shrink-0"
}
const _hoisted_20 = {
  key: 1,
  class: "w-full p-3 text-center text-sm text-gray-400"
}
const _hoisted_21 = {
  key: 2,
  class: "p-4 text-center text-sm text-gray-500"
}
const _hoisted_22 = { class: "text-lg font-bold text-textPrimary text-left" }
const _hoisted_23 = { class: "text-sm text-[#68688D] leading-6 whitespace-pre-line text-left" }
const _hoisted_24 = { class: "flex justify-end gap-4" }

import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { mdiBellOutline, mdiClose } from '@mdi/js'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/pinia-store/useNotificationStore'
import { useModalStore, EModals } from '@/pinia-store/useModalStore'
import { useErrorToast } from '@/composables/useErrorToast'
import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import DoctorNotificationIcon from '@/assets/icons/doctor-notification.vue'
import NotificationItemSkeleton from '@/components/designSystem/Skeleton/NotificationItemSkeleton.vue'
import type { WebNotification } from '@/types/web-notification.types'

const MESSAGE_TRUNCATE_THRESHOLD = 100


export default /*#__PURE__*/_defineComponent({
  __name: 'NotificationModal',
  setup(__props) {

const router = useRouter()
const notificationStore = useNotificationStore()
const modalStore = useModalStore()
const { modals } = storeToRefs(modalStore)
const { notifyError } = useErrorToast()

const notificationOpen = ref(false)
const detailNotification = ref<WebNotification | null>(null)

const notifications = computed(() => notificationStore.notifications)
const unreadCount = computed(() => notificationStore.unreadCount)
const hasMore = computed(() => notificationStore.hasMore)
const isLoading = computed(() => notificationStore.isLoading)

function isMessageLong(message: string): boolean {
  return !!message && message.length > MESSAGE_TRUNCATE_THRESHOLD
}

function toggleNotifications() {
  notificationOpen.value = !notificationOpen.value
  if (notificationOpen.value) {
    notificationStore
      .fetchNotifications()
      .catch((e: unknown) => notifyError(e, 'Não foi possível carregar as notificações.'))
  }
}

/** Marca como lida sem bloquear a navegação; se falhar, a notificação segue não lida. */
function markAsRead(notification: WebNotification) {
  if (notification.isRead) return
  notificationStore
    .markAsRead(notification.id)
    .catch((e: unknown) => notifyError(e, 'Não foi possível marcar a notificação como lida.'))
}

function handleNotificationClick(notification: WebNotification) {
  if (notification.targetUrl) {
    // navigateTo já marca como lida — chamar aqui também duplicaria o PATCH (e o toast).
    notificationOpen.value = false
    navigateTo(notification)
    return
  }
  markAsRead(notification)
}

function isSafeUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) return true

  try {
    const parsed = new URL(url, window.location.origin)
    return parsed.origin === window.location.origin
  } catch {
    return false
  }
}

function navigateTo(notification: WebNotification) {
  markAsRead(notification)
  notificationOpen.value = false
  if (notification.targetUrl) {
    if (!isSafeUrl(notification.targetUrl)) return

    if (notification.targetUrl.startsWith('/')) {
      router.push(notification.targetUrl)
    } else {
      window.open(notification.targetUrl, '_blank', 'noopener,noreferrer')
    }
  }
}

function openDetailModal(notification: WebNotification) {
  markAsRead(notification)
  notificationOpen.value = false
  detailNotification.value = notification
  modalStore.toggleModal(EModals.NOTIFICATION_DETAIL)
}

function handleDetailAction() {
  if (detailNotification.value) {
    navigateTo(detailNotification.value)
  }
  closeDetailModal()
}

function closeDetailModal() {
  modalStore.toggleModal(EModals.NOTIFICATION_DETAIL)
  detailNotification.value = null
}

function handleMarkAllAsRead() {
  notificationStore
    .markAllAsRead()
    .catch((e: unknown) =>
      notifyError(e, 'Não foi possível marcar todas as notificações como lidas.')
    )
}

function handleLoadMore() {
  notificationStore
    .loadMore()
    .catch((e: unknown) => notifyError(e, 'Não foi possível carregar mais notificações.'))
}

function formatTime(dateString: string): string {
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  const now = new Date()
  const diffMs = Math.max(0, now.getTime() - date.getTime())
  const diffMinutes = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMinutes < 1) return 'Agora'
  if (diffMinutes < 60) return `${diffMinutes}min atrás`
  if (diffHours < 24) return `${diffHours}h atrás`
  if (diffDays < 7) return `${diffDays}d atrás`
  return date.toLocaleDateString('pt-BR')
}

onMounted(() => {
  notificationStore.fetchNotifications().catch(() => {
    // fallback intencional: pré-carga em background no mount do Header — abrir o sino
    // refaz a busca (toggleNotifications) e aí a falha vira toast.
  })
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("button", {
          onClick: toggleNotifications,
          class: "flex mx-4 text-gray-600 focus:outline-none mr-6 relative"
        }, [
          _createVNode(_component_v_icon, {
            icon: _unref(mdiBellOutline),
            size: "24",
            color: "primary"
          }, null, 8, ["icon"]),
          (unreadCount.value > 0)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, _toDisplayString(unreadCount.value > 99 ? '99+' : unreadCount.value), 1))
            : _createCommentVNode("", true)
        ]),
        _withDirectives(_createElementVNode("div", {
          onClick: _cache[0] || (_cache[0] = ($event: any) => (notificationOpen.value = false)),
          class: "fixed inset-0 h-full w-full z-30"
        }, null, 512), [
          [_vShow, notificationOpen.value]
        ]),
        _createVNode(_Transition, {
          "enter-active-class": "transition duration-150 ease-out transform",
          "enter-from-class": "scale-95 opacity-0",
          "enter-to-class": "scale-100 opacity-100",
          "leave-active-class": "transition duration-150 ease-in transform",
          "leave-from-class": "scale-100 opacity-100",
          "leave-to-class": "scale-95 opacity-0"
        }, {
          default: _withCtx(() => [
            _withDirectives(_createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  (unreadCount.value > 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(unreadCount.value > 99 ? '99+' : unreadCount.value), 1))
                    : _createCommentVNode("", true),
                  _hoisted_8
                ]),
                _createElementVNode("div", _hoisted_9, [
                  (unreadCount.value > 0)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        onClick: handleMarkAllAsRead,
                        class: "text-xs text-primary hover:text-primary20 font-medium"
                      }, " Marcar todas como lidas "))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (notificationOpen.value = false)),
                    class: "text-gray-400 hover:text-gray-600 transition-colors duration-150"
                  }, [
                    _createVNode(_component_v_icon, {
                      icon: _unref(mdiClose),
                      size: "20"
                    }, null, 8, ["icon"])
                  ])
                ])
              ]),
              (isLoading.value && notifications.value.length === 0)
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (n) => {
                      return _createVNode(NotificationItemSkeleton, {
                        key: `skeleton-${n}`
                      })
                    }), 64))
                  ]))
                : (notifications.value.length > 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(notifications.value, (notification) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: notification.id,
                          class: "relative flex items-start gap-3 p-3 border-b border-gray-100 hover:bg-gray-50",
                          style: _normalizeStyle(!notification.isRead ? { backgroundColor: '#D8EEF3A3' } : {})
                        }, [
                          _createElementVNode("div", _hoisted_12, [
                            _createVNode(DoctorNotificationIcon)
                          ]),
                          _createElementVNode("div", _hoisted_13, [
                            _createElementVNode("p", {
                              class: "text-base font-normal text-secondary50 leading-6 tracking-[0.08px] line-clamp-1 cursor-pointer",
                              onClick: ($event: any) => (handleNotificationClick(notification))
                            }, _toDisplayString(notification.title), 9, _hoisted_14),
                            _createElementVNode("p", {
                              class: "text-sm font-normal text-[#68688D] leading-[18px] tracking-[0.07px] mt-0.5 overflow-hidden cursor-pointer",
                              style: {"display":"-webkit-box","-webkit-line-clamp":"2","-webkit-box-orient":"vertical"},
                              onClick: _withModifiers(($event: any) => (openDetailModal(notification)), ["stop"])
                            }, _toDisplayString(notification.message), 9, _hoisted_15),
                            (isMessageLong(notification.message))
                              ? (_openBlock(), _createElementBlock("button", {
                                  key: 0,
                                  onClick: _withModifiers(($event: any) => (openDetailModal(notification)), ["stop"]),
                                  class: "text-xs text-primary hover:text-primary20 font-medium mt-0.5"
                                }, " Leia mais ", 8, _hoisted_16))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_17, [
                              _createElementVNode("p", _hoisted_18, _toDisplayString(formatTime(notification.createdAt)), 1)
                            ])
                          ]),
                          (!notification.isRead)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_19))
                            : _createCommentVNode("", true)
                        ], 4))
                      }), 128)),
                      (hasMore.value && !isLoading.value)
                        ? (_openBlock(), _createElementBlock("button", {
                            key: 0,
                            onClick: handleLoadMore,
                            class: "w-full p-3 text-sm text-primary hover:bg-gray-50 font-medium"
                          }, " Carregar mais "))
                        : _createCommentVNode("", true),
                      (isLoading.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_20, " Carregando... "))
                        : _createCommentVNode("", true)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_21, " Você não tem nenhuma notificação no momento. "))
            ], 512), [
              [_vShow, notificationOpen.value]
            ])
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(Modal, {
      modalActive: _unref(modals)[_unref(EModals).NOTIFICATION_DETAIL],
      hasHeaderBorder: true,
      maxWidth: "max-w-lg",
      maxHeight: "60vh",
      onClose: closeDetailModal
    }, {
      header: _withCtx(() => [
        _createElementVNode("h3", _hoisted_22, _toDisplayString(detailNotification.value?.title), 1)
      ]),
      footer: _withCtx(() => [
        _createElementVNode("div", _hoisted_24, [
          _createVNode(OutlineButton, {
            text: "Fechar",
            width: "120px",
            height: "42px",
            onClick: closeDetailModal
          }),
          (detailNotification.value?.targetUrl)
            ? (_openBlock(), _createBlock(FilledButton, {
                key: 0,
                text: "Ver detalhes",
                width: "140px",
                height: "42px",
                color: "primary",
                onClick: handleDetailAction
              }))
            : _createCommentVNode("", true)
        ])
      ]),
      default: _withCtx(() => [
        _createElementVNode("p", _hoisted_23, _toDisplayString(detailNotification.value?.message), 1)
      ]),
      _: 1
    }, 8, ["modalActive"])
  ], 64))
}
}

})